import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { getSupabase } from "@/lib/supabase";
import { ShieldPlus } from "lucide-react";
import { useState } from "react";

export function AdminInvites() {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h2 className="text-lg font-semibold tracking-tight">Invites</h2>
        <p className="text-sm text-ink-dim mt-1">
          Tenants are invited automatically when you create their tenancy. Use this page to invite
          additional admins.
        </p>
      </header>
      <InviteAdminCard />
    </div>
  );
}

function InviteAdminCard() {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    setSubmitting(true);
    try {
      const sb = getSupabase();
      const { data: u } = await sb.auth.getUser();
      const inviterName = (u.user?.user_metadata?.full_name as string | undefined) ?? null;
      const { data, error: invokeErr } = await sb.functions.invoke<{
        ok?: boolean;
        status?: "invited" | "reinvited";
        error?: string;
      }>("invite-admin", {
        body: {
          email: email.trim(),
          full_name: fullName.trim() || undefined,
          inviter_name: inviterName ?? undefined,
        },
      });
      if (invokeErr || data?.error) {
        setError(data?.error ?? invokeErr?.message ?? "Could not send the invite");
        return;
      }
      setNotice(
        data?.status === "reinvited"
          ? `Sent a fresh sign-in link to ${email.trim()}.`
          : `Invited ${email.trim()} as an admin.`,
      );
      setEmail("");
      setFullName("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invite an admin</CardTitle>
        <CardDescription>
          Sends a branded magic-link email. The invitee will land on the admin panel signed in, with
          full access to manage the portfolio.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-3 max-w-md" noValidate>
          <Field id="invite-name" label="Full name (optional)">
            <Input
              id="invite-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              autoComplete="name"
            />
          </Field>
          <Field id="invite-email" label="Email">
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              autoComplete="email"
              required
            />
          </Field>
          {error ? (
            <p role="alert" className="text-xs text-red-300">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p role="status" className="text-xs text-emerald-300">
              {notice}
            </p>
          ) : null}
          <div>
            <Button type="submit" size="sm" disabled={submitting}>
              <ShieldPlus size={14} />
              {submitting ? "Inviting…" : "Send invite"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
